<template>
  <div>
    <Govem_bar />
    <v-container fluid class="pa-4 pa-md-8">
      <div class="d-flex flex-wrap align-center justify-space-between mb-6">
        <div>
          <h2 class="font-weight-bold primary--text mb-1">
            <v-icon large color="primary" class="mr-2">mdi-swap-horizontal-bold</v-icon>
            โยกย้ายพนักงานราชการระหว่างสถานศึกษา
          </h2>
          <span class="grey--text text--darken-1 ml-2">
            ส่งพนักงานของเราไปสถานศึกษาอื่น หรือดึงพนักงานจากสถานศึกษาอื่นมาตำแหน่งว่างของเรา —
            ไม่ต้องกรอกข้อมูลใหม่ ระบบย้ายประวัติทั้งหมดให้อัตโนมัติเมื่ออีกฝ่ายยืนยัน
          </span>
        </div>
      </div>

      <v-alert text color="info" border="left" icon="mdi-information-outline" class="mb-6">
        ทุกคำขอ<strong>ต้องให้สถานศึกษาอีกฝ่ายกดยืนยันก่อน</strong> ข้อมูลจึงจะย้ายจริง — ยังไม่ยืนยันสามารถยกเลิกคำขอได้
      </v-alert>

      <v-card class="rounded-xl" elevation="2">
        <v-tabs v-model="activeTab" background-color="transparent" show-arrows>
          <v-tab>สร้างคำขอใหม่</v-tab>
          <v-tab>
            รอฉันยืนยัน
            <v-chip v-if="incoming.length" x-small color="error" text-color="white" class="ml-2">{{ incoming.length }}</v-chip>
          </v-tab>
          <v-tab>
            คำขอของฉัน
            <v-chip v-if="outgoing.length" x-small color="warning" text-color="white" class="ml-2">{{ outgoing.length }}</v-chip>
          </v-tab>
          <v-tab>ประวัติ</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- ========================= สร้างคำขอใหม่ ========================= -->
          <v-tab-item>
            <v-card-text class="pa-6">
              <v-btn-toggle v-model="mode" mandatory color="primary" class="mb-6">
                <v-btn value="export" class="text-capitalize">
                  <v-icon left small>mdi-arrow-right-bold</v-icon>ส่งพนักงานของเราไปสถานศึกษาอื่น
                </v-btn>
                <v-btn value="pull" class="text-capitalize">
                  <v-icon left small>mdi-arrow-left-bold</v-icon>ดึงพนักงานจากสถานศึกษาอื่นมาที่เรา
                </v-btn>
                <v-btn value="swap" class="text-capitalize">
                  <v-icon left small>mdi-swap-horizontal</v-icon>สับเปลี่ยนกับสถานศึกษาอื่น
                </v-btn>
                <v-btn value="reallocate" class="text-capitalize">
                  <v-icon left small>mdi-send-outline</v-icon>เกลี่ยตำแหน่งว่างไปสถานศึกษาอื่น
                </v-btn>
              </v-btn-toggle>

              <v-alert v-if="mode === 'swap'" text dense color="info" class="mb-4">
                สับเปลี่ยน: พนักงานของเราและพนักงานของสถานศึกษาอื่น (ทั้งคู่ต้องมีผู้ครองตำแหน่งอยู่แล้ว) จะสลับที่ทำงานกันโดยตรง
              </v-alert>
              <v-alert v-if="mode === 'reallocate'" text dense color="info" class="mb-4">
                เกลี่ยตำแหน่ง: ย้ายกรอบอัตรากำลัง "ตำแหน่งว่าง" ของเราไปสังกัดสถานศึกษาอื่น โดยไม่มีคนเกี่ยวข้อง ใช้เมื่อสถานศึกษาอื่นขาดแคลนกว่าเรา
              </v-alert>

              <v-form ref="form">
                <v-row v-if="mode === 'export'">
                  <v-col cols="12" md="6">
                    <div class="step-label">1. เลือกพนักงานของเราที่จะส่งออก</div>
                    <v-autocomplete
                      v-model="myEmployee"
                      :items="myEmployees"
                      :item-text="employeeLabel"
                      return-object
                      outlined
                      dense
                      :loading="loadingMyEmployees"
                      label="พนักงานราชการ (ของเรา)"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="ไม่พบข้อมูล หรือยังไม่ได้โหลด"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="step-label">2. เลือกสถานศึกษาปลายทาง</div>
                    <v-autocomplete
                      v-model="otherCollege"
                      :items="collegeOptions"
                      item-text="college_name"
                      item-value="college_code"
                      outlined
                      dense
                      :loading="loadingColleges"
                      label="สถานศึกษาปลายทาง"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      @change="loadOtherPositions"
                    />
                  </v-col>
                  <v-col cols="12" md="6" offset-md="6">
                    <div class="step-label">3. เลือกตำแหน่งว่างปลายทาง</div>
                    <v-autocomplete
                      v-model="otherVacant"
                      :items="otherVacantPositions"
                      :item-text="vacantLabel"
                      return-object
                      outlined
                      dense
                      :disabled="!otherCollege"
                      :loading="loadingOtherPositions"
                      label="ตำแหน่งว่างที่ปลายทาง"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="ไม่พบตำแหน่งว่างที่สถานศึกษานี้"
                    />
                  </v-col>
                </v-row>

                <v-row v-else-if="mode === 'pull'">
                  <v-col cols="12" md="6">
                    <div class="step-label">1. เลือกตำแหน่งว่างของเรา</div>
                    <v-autocomplete
                      v-model="myVacant"
                      :items="myVacantPositions"
                      :item-text="vacantLabel"
                      return-object
                      outlined
                      dense
                      :loading="loadingMyEmployees"
                      label="ตำแหน่งว่าง (ของเรา)"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="สถานศึกษาของเราไม่มีตำแหน่งว่างในขณะนี้"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="step-label">2. เลือกสถานศึกษาต้นทาง</div>
                    <v-autocomplete
                      v-model="otherCollege"
                      :items="collegeOptions"
                      item-text="college_name"
                      item-value="college_code"
                      outlined
                      dense
                      :loading="loadingColleges"
                      label="สถานศึกษาต้นทาง"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      @change="loadOtherEmployees"
                    />
                  </v-col>
                  <v-col cols="12" md="6" offset-md="6">
                    <div class="step-label">3. เลือกพนักงานที่จะดึงเข้า</div>
                    <v-autocomplete
                      v-model="otherEmployee"
                      :items="otherEmployees"
                      :item-text="employeeLabel"
                      return-object
                      outlined
                      dense
                      :disabled="!otherCollege"
                      :loading="loadingOtherEmployees"
                      label="พนักงานต้นทาง"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="ไม่พบพนักงานราชการที่สถานศึกษานี้"
                    />
                  </v-col>
                </v-row>

                <!-- สับเปลี่ยน: พนักงานของเรา (ต้องมีผู้ครองจริง) <-> พนักงานของอีกฝ่าย (ต้องมีผู้ครองจริงเช่นกัน) -->
                <v-row v-else-if="mode === 'swap'">
                  <v-col cols="12" md="6">
                    <div class="step-label">1. เลือกพนักงานของเราที่จะขอสับเปลี่ยน</div>
                    <v-autocomplete
                      v-model="myEmployee"
                      :items="myEmployees"
                      :item-text="employeeLabel"
                      return-object
                      outlined
                      dense
                      :loading="loadingMyEmployees"
                      label="พนักงานราชการ (ของเรา)"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="ไม่พบข้อมูล หรือยังไม่ได้โหลด"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="step-label">2. เลือกสถานศึกษาอีกฝ่าย</div>
                    <v-autocomplete
                      v-model="otherCollege"
                      :items="collegeOptions"
                      item-text="college_name"
                      item-value="college_code"
                      outlined
                      dense
                      :loading="loadingColleges"
                      label="สถานศึกษาอีกฝ่าย"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      @change="loadOtherEmployees"
                    />
                  </v-col>
                  <v-col cols="12" md="6" offset-md="6">
                    <div class="step-label">3. เลือกพนักงานที่จะขอสับเปลี่ยนด้วย</div>
                    <v-autocomplete
                      v-model="otherEmployee"
                      :items="otherEmployees"
                      :item-text="employeeLabel"
                      return-object
                      outlined
                      dense
                      :disabled="!otherCollege"
                      :loading="loadingOtherEmployees"
                      label="พนักงานของอีกฝ่าย"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="ไม่พบพนักงานราชการที่สถานศึกษานี้"
                    />
                  </v-col>
                </v-row>

                <!-- เกลี่ยตำแหน่ง: ย้ายตำแหน่งว่างของเราไปสังกัดสถานศึกษาอื่น ไม่มีคนเกี่ยวข้อง จึงไม่มีขั้นที่ 3 -->
                <v-row v-else>
                  <v-col cols="12" md="6">
                    <div class="step-label">1. เลือกตำแหน่งว่างของเราที่จะเกลี่ยออก</div>
                    <v-autocomplete
                      v-model="myVacant"
                      :items="myVacantPositions"
                      :item-text="vacantLabel"
                      return-object
                      outlined
                      dense
                      :loading="loadingMyEmployees"
                      label="ตำแหน่งว่าง (ของเรา)"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                      no-data-text="สถานศึกษาของเราไม่มีตำแหน่งว่างในขณะนี้"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="step-label">2. เลือกสถานศึกษาปลายทาง</div>
                    <v-autocomplete
                      v-model="otherCollege"
                      :items="collegeOptions"
                      item-text="college_name"
                      item-value="college_code"
                      outlined
                      dense
                      :loading="loadingColleges"
                      label="สถานศึกษาปลายทาง"
                      :rules="[v => !!v || 'กรุณาเลือก']"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="note"
                      outlined
                      dense
                      rows="2"
                      label="หมายเหตุ (ไม่บังคับ)"
                      placeholder="เช่น เลขที่คำสั่ง/หนังสืออ้างอิง"
                    />
                  </v-col>
                </v-row>

                <!-- สรุปก่อนส่งคำขอ -->
                <v-alert v-if="summaryText" text color="primary" border="left" class="mb-4">
                  {{ summaryText }}
                </v-alert>

                <v-btn color="primary" large :loading="submitting" @click="submitRequest">
                  <v-icon left small>mdi-send</v-icon>ส่งคำขอ (รอสถานศึกษาอีกฝ่ายยืนยัน)
                </v-btn>
              </v-form>
            </v-card-text>
          </v-tab-item>

          <!-- ========================= รอฉันยืนยัน ========================= -->
          <v-tab-item>
            <v-card-text class="pa-4 pa-md-6">
              <div v-if="!incoming.length" class="text-center grey--text pa-8">
                <v-icon size="56" color="grey lighten-2">mdi-check-circle-outline</v-icon>
                <div class="mt-2">ไม่มีคำขอที่รอการยืนยันจากสถานศึกษาของท่าน</div>
              </div>
              <v-card v-for="r in incoming" :key="r.id" outlined rounded="lg" class="mb-3 pa-4">
                <RequestSummary :r="r" :my-college="myCollege" />
                <v-card-actions class="px-0 pb-0">
                  <v-btn color="success" depressed @click="confirmRequest(r)">
                    <v-icon left small>mdi-check</v-icon>ยืนยันการโยกย้าย
                  </v-btn>
                  <v-btn color="error" text @click="openReject(r)">
                    <v-icon left small>mdi-close</v-icon>ปฏิเสธ
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-card-text>
          </v-tab-item>

          <!-- ========================= คำขอของฉัน ========================= -->
          <v-tab-item>
            <v-card-text class="pa-4 pa-md-6">
              <div v-if="!outgoing.length" class="text-center grey--text pa-8">
                <v-icon size="56" color="grey lighten-2">mdi-clock-outline</v-icon>
                <div class="mt-2">ยังไม่มีคำขอที่สร้างไว้ หรือรอการตอบรับ</div>
              </div>
              <v-card v-for="r in outgoing" :key="r.id" outlined rounded="lg" class="mb-3 pa-4">
                <RequestSummary :r="r" :my-college="myCollege" />
                <v-chip small color="warning" text-color="white" class="mt-2">รอสถานศึกษาอีกฝ่ายยืนยัน</v-chip>
                <v-card-actions class="px-0 pb-0">
                  <v-btn color="error" text @click="cancelRequest(r)">
                    <v-icon left small>mdi-close-circle</v-icon>ยกเลิกคำขอ
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-card-text>
          </v-tab-item>

          <!-- ========================= ประวัติ ========================= -->
          <v-tab-item>
            <v-card-text class="pa-4 pa-md-6">
              <div v-if="!history.length" class="text-center grey--text pa-8">ยังไม่มีประวัติการโยกย้าย</div>
              <v-card v-for="r in history" :key="r.id" outlined rounded="lg" class="mb-3 pa-4">
                <RequestSummary :r="r" :my-college="myCollege" />
                <v-chip
                  small
                  :color="r.status === 'confirmed' ? 'success' : r.status === 'rejected' ? 'error' : 'grey'"
                  text-color="white"
                  class="mt-2"
                >
                  {{ statusLabel(r.status) }}
                </v-chip>
                <div v-if="r.reject_reason" class="text-caption grey--text mt-1">เหตุผล: {{ r.reject_reason }}</div>
              </v-card>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Dialog ปฏิเสธคำขอ -->
      <v-dialog v-model="rejectDialog" max-width="480">
        <v-card>
          <v-card-title>ระบุเหตุผลการปฏิเสธ (ไม่บังคับ)</v-card-title>
          <v-card-text>
            <v-textarea v-model="rejectReason" outlined dense rows="3" label="เหตุผล" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="rejectDialog = false">ยกเลิก</v-btn>
            <v-btn color="error" @click="submitReject">ยืนยันการปฏิเสธ</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Govem_bar from "../../../components/college/govem_bar.vue";

// แสดงสรุปรายการคำขอ 1 แถว — ใช้ร่วมกันทั้ง 3 แท็บ (รอยืนยัน/คำขอของฉัน/ประวัติ)
const RequestSummary = {
  props: { r: Object, myCollege: String },
  computed: {
    isMine() {
      // มุมมองของสถานศึกษาเรา: ฝั่งไหนคือ "เรา" เพื่อจัดลูกศรทิศทางให้สื่อความหมายถูกต้อง
      return this.r.source_college_code === this.myCollege ? "source" : "target";
    }
  },
  render(h) {
    const r = this.r;
    const directionMeta = {
      export: { label: "ส่งออก", color: "primary" },
      pull: { label: "ดึงเข้า", color: "primary" },
      swap: { label: "สับเปลี่ยน", color: "info" },
      reallocate: { label: "เกลี่ยตำแหน่ง", color: "success" }
    }[r.direction] || { label: r.direction, color: "grey" };
    const fmtDate = s => {
      if (!s) return "-";
      const d = new Date(s.replace(" ", "T"));
      return isNaN(d.getTime()) ? s : d.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
    };
    return h("div", [
      h("v-chip", { props: { xSmall: true, label: true, color: directionMeta.color, dark: true }, class: "mb-2" }, [directionMeta.label]),
      h("div", { class: "font-weight-bold" },
        r.direction === "reallocate"
          ? ["ตำแหน่งว่างเลขที่ ", r.source_id_position, " — ", r.source_name_postion || ""]
          : [r.source_full_name || "(ไม่ทราบชื่อ)", " — ", r.source_name_postion || ""]
      ),
      h("div", { class: "text-body-2 grey--text text--darken-2 mt-1" }, [
        "จาก ", h("strong", r.source_college_code), " (เลขตำแหน่ง ", r.source_id_position, ")",
        " ", h("v-icon", { props: { small: true } }, [r.direction === "swap" ? "mdi-swap-horizontal" : "mdi-arrow-right"]),
        " ไป ", h("strong", r.target_college_code), r.target_id_position ? [" (เลขตำแหน่ง ", r.target_id_position, ")"] : ""
      ]),
      h("div", { class: "text-caption grey--text mt-1" }, [
        "สร้างคำขอโดย ", r.initiated_by_college, " เมื่อ ", fmtDate(r.requested_at),
        r.note ? " · หมายเหตุ: " + r.note : ""
      ])
    ]);
  }
};

export default {
  name: "GovEmTransferRequests",
  components: { Govem_bar, RequestSummary },
  data() {
    return {
      ApiKey: "HRvec2021",
      user: {},
      myCollege: "",

      activeTab: 0,
      mode: "export",
      note: "",
      submitting: false,

      myEmployees: [],
      myVacantPositions: [],
      loadingMyEmployees: false,
      myEmployee: null,
      myVacant: null,

      collegeOptions: [],
      loadingColleges: false,
      otherCollege: null,

      otherVacantPositions: [],
      loadingOtherPositions: false,
      otherVacant: null,

      otherEmployees: [],
      loadingOtherEmployees: false,
      otherEmployee: null,

      incoming: [],
      outgoing: [],
      history: [],

      rejectDialog: false,
      rejectReason: "",
      rejectTarget: null
    };
  },
  computed: {
    summaryText() {
      if (this.mode === "export" && this.myEmployee && this.otherVacant) {
        return `${this.employeeLabel(this.myEmployee)} จะย้ายจาก ${this.myCollege} ไปดำรงตำแหน่ง ${this.otherVacant.id_position} ที่ ${this.otherCollegeName}`;
      }
      if (this.mode === "pull" && this.myVacant && this.otherEmployee) {
        return `${this.employeeLabel(this.otherEmployee)} จะย้ายจาก ${this.otherCollegeName} มาดำรงตำแหน่ง ${this.myVacant.id_position} ที่ ${this.myCollege}`;
      }
      if (this.mode === "swap" && this.myEmployee && this.otherEmployee) {
        return `${this.employeeLabel(this.myEmployee)} (${this.myCollege}) จะสลับที่ทำงานกับ ${this.employeeLabel(this.otherEmployee)} (${this.otherCollegeName})`;
      }
      if (this.mode === "reallocate" && this.myVacant && this.otherCollege) {
        return `ตำแหน่งว่างเลขที่ ${this.myVacant.id_position} จะย้ายจาก ${this.myCollege} ไปสังกัด ${this.otherCollegeName}`;
      }
      return "";
    },
    otherCollegeName() {
      const c = this.collegeOptions.find(c => c.college_code === this.otherCollege);
      return c ? c.college_name : this.otherCollege;
    }
  },
  watch: {
    mode() {
      this.myEmployee = null;
      this.myVacant = null;
      this.otherCollege = null;
      this.otherVacant = null;
      this.otherEmployee = null;
      this.otherVacantPositions = [];
      this.otherEmployees = [];
    }
  },
  async mounted() {
    await this.userQuery();
    await Promise.all([this.loadMyRoster(), this.loadColleges(), this.loadRequests()]);
    this.applyQueryPrefill();
  },
  methods: {
    employeeLabel(e) {
      if (!e) return "";
      const name = `${e.title_s || ""}${e.frist_name || ""} ${e.last_name || ""}`.trim();
      return `${name} — ${e.name_postion || ""} (เลขตำแหน่ง ${e.id_position})`;
    },
    vacantLabel(e) {
      if (!e) return "";
      return `เลขตำแหน่ง ${e.id_position} — ${e.name_postion || "ไม่ระบุชื่อตำแหน่ง"}`;
    },
    statusLabel(s) {
      return { confirmed: "ยืนยันแล้ว / ย้ายสำเร็จ", rejected: "ถูกปฏิเสธ", cancelled: "ยกเลิกแล้ว" }[s] || s;
    },

    async userQuery() {
      const userSession = JSON.parse(localStorage.getItem("user")) || {};
      const result = await this.$http.post("user.php", { ApiKey: this.ApiKey, user_ID: userSession.user_ID });
      this.user = result.data || {};
      this.myCollege = this.user.user_code || "";
    },

    async loadMyRoster() {
      this.loadingMyEmployees = true;
      try {
        const result = await this.$http.post("government_employee.php", {
          ApiKey: this.ApiKey,
          college_code: this.myCollege
        });
        const rows = Array.isArray(result.data) ? result.data : [];
        this.myEmployees = rows.filter(r => String(r.id_card) !== "0");
        this.myVacantPositions = rows.filter(r => String(r.id_card) === "0");
      } finally {
        this.loadingMyEmployees = false;
      }
    },

    async loadColleges() {
      this.loadingColleges = true;
      try {
        const result = await this.$http.post("college_directory.php", { ApiKey: this.ApiKey });
        const rows = Array.isArray(result.data) ? result.data : [];
        this.collegeOptions = rows.filter(c => c.college_code !== this.myCollege);
      } finally {
        this.loadingColleges = false;
      }
    },

    async loadOtherPositions() {
      this.otherVacant = null;
      this.otherVacantPositions = [];
      if (!this.otherCollege) return;
      this.loadingOtherPositions = true;
      try {
        const result = await this.$http.post("government_employee.php", {
          ApiKey: this.ApiKey,
          college_code: this.otherCollege,
          id_card_null: "1"
        });
        this.otherVacantPositions = Array.isArray(result.data) ? result.data : [];
      } finally {
        this.loadingOtherPositions = false;
      }
    },

    async loadOtherEmployees() {
      this.otherEmployee = null;
      this.otherEmployees = [];
      if (!this.otherCollege) return;
      this.loadingOtherEmployees = true;
      try {
        const result = await this.$http.post("government_employee.php", {
          ApiKey: this.ApiKey,
          college_code: this.otherCollege
        });
        const rows = Array.isArray(result.data) ? result.data : [];
        this.otherEmployees = rows.filter(r => String(r.id_card) !== "0");
      } finally {
        this.loadingOtherEmployees = false;
      }
    },

    async loadRequests() {
      const result = await this.$http.post("government_employee_transfer.list.php", { ApiKey: this.ApiKey });
      if (result.data && result.data.status) {
        this.incoming = result.data.incoming || [];
        this.outgoing = result.data.outgoing || [];
        this.history = result.data.history || [];
      }
    },

    async submitRequest() {
      if (!this.$refs.form.validate()) return;
      this.submitting = true;
      try {
        const payload = { ApiKey: this.ApiKey, direction: this.mode, note: this.note };
        if (this.mode === "export") {
          payload.source_id_no = this.myEmployee.id_no;
          payload.target_id_no = this.otherVacant.id_no;
        } else if (this.mode === "pull") {
          payload.source_id_no = this.otherEmployee.id_no;
          payload.target_id_no = this.myVacant.id_no;
        } else if (this.mode === "swap") {
          payload.source_id_no = this.myEmployee.id_no;
          payload.target_id_no = this.otherEmployee.id_no;
        } else {
          // reallocate: ไม่มีแถวปลายทาง ส่งแค่รหัสสถานศึกษาปลายทาง
          payload.source_id_no = this.myVacant.id_no;
          payload.target_college_code = this.otherCollege;
        }
        const result = await this.$http.post("government_employee_transfer.request.php", payload);
        if (result.data.status) {
          Swal.fire({ icon: "success", title: "ส่งคำขอเรียบร้อย", text: "รอสถานศึกษาอีกฝ่ายกดยืนยัน", timer: 2000, showConfirmButton: false });
          this.myEmployee = null;
          this.myVacant = null;
          this.otherCollege = null;
          this.otherVacant = null;
          this.otherEmployee = null;
          this.note = "";
          await Promise.all([this.loadRequests(), this.loadMyRoster()]);
          this.activeTab = 2;
        } else {
          Swal.fire({ icon: "warning", title: "ส่งคำขอไม่สำเร็จ", text: result.data.message || "" });
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ" });
      } finally {
        this.submitting = false;
      }
    },

    async confirmRequest(r) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "ยืนยันการโยกย้ายนี้?",
        html: `${r.source_full_name} (${r.source_college_code}) → เลขตำแหน่ง ${r.target_id_position} ที่ ${r.target_college_code}<br><strong>เมื่อยืนยันแล้วไม่สามารถย้อนกลับผ่านหน้านี้ได้</strong>`,
        showCancelButton: true,
        confirmButtonText: "ยืนยันการโยกย้าย",
        cancelButtonText: "ยกเลิก"
      });
      if (!confirm.isConfirmed) return;
      const result = await this.$http.post("government_employee_transfer.confirm.php", { ApiKey: this.ApiKey, id: r.id });
      if (result.data.status) {
        Swal.fire({ icon: "success", title: "โยกย้ายสำเร็จ", timer: 2000, showConfirmButton: false });
        await Promise.all([this.loadRequests(), this.loadMyRoster()]);
      } else {
        Swal.fire({ icon: "error", title: "ไม่สำเร็จ", text: result.data.message || "" });
        await this.loadRequests();
      }
    },

    openReject(r) {
      this.rejectTarget = r;
      this.rejectReason = "";
      this.rejectDialog = true;
    },
    async submitReject() {
      const result = await this.$http.post("government_employee_transfer.reject.php", {
        ApiKey: this.ApiKey,
        id: this.rejectTarget.id,
        reason: this.rejectReason
      });
      this.rejectDialog = false;
      if (result.data.status) {
        Swal.fire({ icon: "success", title: "ปฏิเสธคำขอเรียบร้อย", timer: 1500, showConfirmButton: false });
        await this.loadRequests();
      } else {
        Swal.fire({ icon: "warning", title: "ดำเนินการไม่สำเร็จ", text: result.data.message || "" });
      }
    },

    async cancelRequest(r) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "ยกเลิกคำขอนี้?",
        showCancelButton: true,
        confirmButtonText: "ยกเลิกคำขอ",
        cancelButtonText: "ไม่"
      });
      if (!confirm.isConfirmed) return;
      const result = await this.$http.post("government_employee_transfer.cancel.php", { ApiKey: this.ApiKey, id: r.id });
      if (result.data.status) {
        Swal.fire({ icon: "success", title: "ยกเลิกคำขอแล้ว", timer: 1500, showConfirmButton: false });
        await this.loadRequests();
      } else {
        Swal.fire({ icon: "warning", title: "ดำเนินการไม่สำเร็จ", text: result.data.message || "" });
      }
    },

    // รองรับลิงก์ลัดจากหน้า government_employee.vue
    // (?mode=export&source_id_no=101 / ?mode=pull&target_id_no=102 / ?mode=swap&source_id_no=101 / ?mode=reallocate&target_id_no=102)
    applyQueryPrefill() {
      const q = this.$route.query;
      if (["export", "pull", "swap", "reallocate"].includes(q.mode)) this.mode = q.mode;
      if ((q.mode === "export" || q.mode === "swap") && q.source_id_no) {
        this.myEmployee = this.myEmployees.find(e => String(e.id_no) === String(q.source_id_no)) || null;
      }
      if ((q.mode === "pull" || q.mode === "reallocate") && q.target_id_no) {
        this.myVacant = this.myVacantPositions.find(e => String(e.id_no) === String(q.target_id_no)) || null;
      }
    }
  }
};
</script>

<style scoped>
.step-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #1b365d;
  margin-bottom: 6px;
}
</style>
